import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

/** A campus as a member may choose it: the church's active campuses, in the church's order. */
export interface ChurchCampus {
  readonly id: string;
  readonly name: string;
}

/**
 * The church's campuses, for the member's own choice (ADR 0036 amendment, the member's half). Every member of the
 * church may read them; the list is empty for a church that has not set any up beyond its own.
 */
export function useChurchCampuses() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["church-campuses", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<ChurchCampus[]> => {
      const { data, error } = await supabase
        .from("campuses")
        .select("id, name")
        .eq("tenant_id", tenantId ?? "")
        .eq("is_active", true)
        .order("sort_order")
        .order("name");
      if (error) throw error;
      return data;
    },
  });
}

/**
 * The member picks, changes or clears their own home campus (`set_my_campus`). The church's record of where they
 * worship follows, and so do the portal's lists: groups and events at their campus come first.
 */
export function useSetMyCampus() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (campusId: string | null): Promise<void> => {
      const { error } = await supabase.rpc("set_my_campus", {
        p_tenant_id: tenantId ?? "",
        // Null clears the choice: the function takes it as "no home campus". The generated type has no default for it.
        p_campus_id: campusId as string,
      });
      if (error) throw error;
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ["my-profile", tenantId] }),
  });
}

/**
 * Whether a record at this campus belongs in a list filtered to `chosen` (a campus name, or null for every campus).
 * A record with no campus is open to all campuses (ADR 0036) and is always shown, as Church Center shows the
 * groups "that haven't been assigned to a specific campus" under every campus.
 */
export function atCampus(recordCampus: string | null | undefined, chosen: string | null): boolean {
  return (
    chosen === null ||
    recordCampus === null ||
    recordCampus === undefined ||
    recordCampus === chosen
  );
}
